import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { AuthService } from '../../auth/auth/auth.service';
import { AppConfigService } from '../../shared/appconfig/appconfig.service';
import { Observable, of } from 'rxjs';
import { timeout, catchError, map } from 'rxjs/operators';

import {
  OnboardingModel,
  OnboardingValidationResult,
  Step1Model,
  Step2Model,
  Step3Model,
  Step4Model,
  Step5Model,
  PsaOptionModel,
  ServiceChannelOptionModel,
  TeamsAndChannelsModel
} from '../models/onboarding.model';
import { PSA_OPTIONS } from '../models/psa-configurations';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('BASE_URL') baseUrl: string) {
  }

  private headers: HttpHeaders;

  private getUrl(): string {
    return AppConfigService.settings.get('chatstyleapiServer');
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Authorization': this.authService.getAuthorizationHeaderValue() });
  }

  // Empty model factories
  emptyOnboardingModel(): OnboardingModel {
    return {
      id: '',
      organizationId: '',
      userId: '',
      createdAt: new Date(),
      currentStep: 1,
      isCompleted: false
    };
  }

  emptyStep1Model(): Step1Model {
    return {} as Step1Model;
  }

  emptyStep2Model(): Step2Model {
    return {
      selectedPsaId: '',
      psaConfiguration: {}
    };
  }

  emptyStep3Model(): Step3Model {
    return {
      defaultBoard: '',
      defaultStatus: '',
      ticketSource: 'CloudRadial Chat',
      allowedBoards: [],
      enablePriorityMapping: false,
      autoAssign: false,
      notifyCreation: true,
      syncStatus: true,
      criticalPriority: 'high',
      highPriority: 'high',
      mediumPriority: 'medium',
      lowPriority: 'low'
    };
  }

  emptyStep4Model(): Step4Model {
    return {
      selectedChannelType: '',
      channelName: '',
      welcomeMessage: '',
      responder: 'dialog',
      dialog: 'welcome',
      persona: 'default',
      allowedUrl: '',
      avatarUrl: '',
      channelConfiguration: {}
    };
  }

  emptyStep5Model(): Step5Model {
    return {
      enableSalesChat: false
    };
  }

  // API Methods

  /**
   * Initialize a new onboarding session
   */
  initializeOnboarding(): Observable<OnboardingModel> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingModel>(
      this.getUrl() + 'onboarding/initialize',
      {},
      { headers: this.headers }
    );
  }

  /**
   * Get current onboarding state
   */
  getOnboardingState(): Observable<OnboardingModel> {
    this.headers = this.getHeaders();
    return this.http.get<OnboardingModel>(
      this.getUrl() + 'onboarding/state',
      { headers: this.headers }
    );
  }

  /**
   * Save Step 1 data (Company & User Information)
   */
  saveStep1(step1Data: Step1Model): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step1',
      step1Data,
      { headers: this.headers }
    );
  }

  /**
   * Clear Step 1 data (removes company & user information from onboarding state)
   */
  clearStep1(): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.delete<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step1',
      { headers: this.headers }
    );
  }

  /**
   * Clear Step 3 data (removes PSA customization from onboarding state)
   */
  clearStep3(): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.delete<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step3',
      { headers: this.headers }
    );
  }

  /**
   * Send phone verification code via SMS
   */
  sendPhoneVerificationCode(phoneNumber: string): Observable<any> {
    this.headers = this.getHeaders();
    return this.http.post<any>(
      this.getUrl() + 'onboarding/phone/send-code',
      { phoneNumber },
      { headers: this.headers }
    );
  }

  /**
   * Verify phone verification code
   */
  verifyPhoneCode(phoneNumber: string, verificationCode: string): Observable<any> {
    this.headers = this.getHeaders();
    return this.http.post<any>(
      this.getUrl() + 'onboarding/phone/verify-code',
      { phoneNumber, verificationCode },
      { headers: this.headers }
    );
  }

  /**
   * Save Step 2 data (PSA Selection)
   */
  saveStep2(step2Data: Step2Model): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step2',
      step2Data,
      { headers: this.headers }
    );
  }

  /**
   * Validate Step 2 data with PSA connector testing (extended timeout)
   */
  validateStep2WithTest(step2Data: Step2Model): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step2/validate-with-test',
      step2Data,
      {
        headers: this.headers
      }
    ).pipe(
      timeout(300000) // 5 minute timeout for PSA testing
    );
  }

  /**
   * Save Step 3 data (PSA Customization)
   */
  saveStep3(step3Data: Step3Model): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step3',
      step3Data,
      { headers: this.headers }
    );
  }

  /**
   * Create a client channel (UCP or Web)
   */
  createClientChannel(channelData: any): Observable<any> {
    this.headers = this.getHeaders();
    return this.http.post<any>(
      this.getUrl() + 'onboarding/step4/create-channel',
      channelData,
      { headers: this.headers }
    );
  }

  /**
   * Generate embed script for an existing channel
   */
  generateChannelScript(): Observable<{ scriptText: string }> {
    this.headers = this.getHeaders();
    return this.http.get<{ scriptText: string }>(
      this.getUrl() + 'onboarding/step4/generate-script',
      { headers: this.headers }
    );
  }

  /**
   * Save Step 4 data (Web Channel Configuration)
   */
  saveStep4(step4Data: Step4Model): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step4',
      step4Data,
      { headers: this.headers }
    );
  }

  /**
   * Save Step 5 data (Service Channel Setup) and complete onboarding
   */
  saveStep5AndComplete(step5Data: Step5Model): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step5',
      step5Data,
      { headers: this.headers }
    );
  }

  /**
   * Complete Step 5 with delegate approval pending (user sent consent email to admin)
   */
  completeDelegateStep5(channelType: string, delegateEmail: string): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step5/delegate-pending',
      { channelType, delegateEmail },
      { headers: this.headers }
    );
  }

  /**
   * Skip Step 5 (Service Channel Setup) and complete onboarding with Live Chat disabled
   */
  skipStep5AndComplete(): Observable<OnboardingValidationResult> {
    this.headers = this.getHeaders();
    return this.http.post<OnboardingValidationResult>(
      this.getUrl() + 'onboarding/step5/skip',
      {},
      { headers: this.headers }
    );
  }

  /**
   * Get available PSA options
   */
  getPsaOptions(): Observable<PsaOptionModel[]> {
    return of(PSA_OPTIONS.filter(psa => psa.isActive));
  }

  /**
   * Get available service channel options
   */
  getServiceChannelOptions(): Observable<ServiceChannelOptionModel[]> {
    this.headers = this.getHeaders();
    return this.http.get<ServiceChannelOptionModel[]>(
      this.getUrl() + 'onboarding/service-channel-options',
      { headers: this.headers }
    );
  }

  /**
   * Test PSA configuration
   */
  testPSAConfiguration(psaId: string, psaConfiguration: { [key: string]: string }): Observable<{ isValid: boolean; message: string }> {
    this.headers = this.getHeaders();

    const testRequest = {
      psaId: psaId,
      configuration: psaConfiguration
    };

    return this.http.post<{ isValid: boolean; message: string }>(
      this.getUrl() + 'onboarding/test-psa-configuration',
      testRequest,
      { headers: this.headers }
    ).pipe(
      timeout(300000), // 5 minute timeout for PSA testing
      catchError((error) => {
        console.error('PSA testing error:', error);

        // Handle timeout errors specifically
        if (error.name === 'TimeoutError' || (error.error && error.error.message && error.error.message.includes('timeout'))) {
          return of({
            isValid: false,
            message: 'PSA connection test timed out. Please verify your credentials and network connection.'
          });
        }

        // Handle API errors
        if (error.status >= 400 && error.status < 500) {
          const errorMessage = error.error?.message || error.message || 'Invalid PSA configuration';
          return of({
            isValid: false,
            message: errorMessage
          });
        }

        // Handle server errors
        if (error.status >= 500) {
          return of({
            isValid: false,
            message: 'Server error during PSA testing. Please try again later.'
          });
        }

        // Handle network errors
        if (error.status === 0) {
          return of({
            isValid: false,
            message: 'Unable to connect to the server. Please check your network connection.'
          });
        }

        // Generic error handling
        return of({
          isValid: false,
          message: `PSA connection test failed: ${error.message || 'Unknown error'}`
        });
      })
    );
  }

  /**
   * Get teams and channels for a specific service channel integration
   */
  getTeamsAndChannels(connectorId?: string): Observable<TeamsAndChannelsModel> {
    this.headers = this.getHeaders();
    const url = connectorId
      ? `${this.getUrl()}onboarding/teams-and-channels?connectorId=${encodeURIComponent(connectorId)}`
      : `${this.getUrl()}onboarding/teams-and-channels`;
    return this.http.get<TeamsAndChannelsModel>(url, { headers: this.headers });
  }

  /**
   * Refresh Slack channels by re-running the connector test
   */
  refreshSlackChannels(): Observable<{ isValid: boolean; message: string }> {
    this.headers = this.getHeaders();
    return this.http.post<{ isValid: boolean; message: string }>(
      `${this.getUrl()}onboarding/refresh-slack-channels`,
      {},
      { headers: this.headers }
    );
  }

  /**
   * Get flat list of Slack channels from the org's Slack connector metadata
   */
  getSlackChannels(): Observable<{ id: string; name: string }[]> {
    this.headers = this.getHeaders();
    return this.http.get<{ id: string; name: string }[]>(
      `${this.getUrl()}onboarding/slack-channels`,
      { headers: this.headers }
    );
  }

  /**
   * Refresh channels by testing the Internal connector
   */
  refreshChannels(): Observable<{ isValid: boolean; message: string }> {
    this.headers = this.getHeaders();
    return this.http.post<{ isValid: boolean; message: string }>(
      this.getUrl() + 'onboarding/refresh-channels',
      {},
      { headers: this.headers }
    );
  }

  /**
   * Get PSA metadata (boards, statuses, priorities)
   */
  getPsaMetadata(): Observable<any> {
    this.headers = this.getHeaders();
    return this.http.get<any>(
      this.getUrl() + 'onboarding/step3/psa-metadata',
      { headers: this.headers }
    );
  }

  /**
   * Get board-specific metadata (statuses, types, priorities)
   */
  getBoardMetadata(boardName: string): Observable<any> {
    this.headers = this.getHeaders();
    return this.http.get<any>(
      this.getUrl() + `onboarding/step3/board-metadata/${encodeURIComponent(boardName)}`,
      { headers: this.headers }
    );
  }

  /**
   * Activate bot in selected service channel (Teams or Slack)
   * @param botChannelId Optional specific bot channel ID (for multiple service channels per connector)
   */
  activateServiceChannel(
    channelType: 'teams' | 'slack',
    teamId: string,
    channelId: string,
    teamName?: string,
    channelName?: string,
    botChannelId?: string
  ): Observable<any> {
    this.headers = this.getHeaders();

    const body = channelType === 'teams'
      ? { channelType, teamId, channelId, teamName, channelName, botChannelId }
      : { channelType, channelId, channelName, botChannelId };

    return this.http.post(
      this.getUrl() + 'onboarding/step5/activate',
      body,
      { headers: this.headers }
    );
  }

  /**
   * Get connector status for Step 4 (Teams/Slack approval status)
   */
  getConnectorStatus(connectorId?: string): Observable<any> {
    this.headers = this.getHeaders();
    const url = connectorId
      ? `${this.getUrl()}onboarding/step4/connector-status?connectorId=${encodeURIComponent(connectorId)}`
      : `${this.getUrl()}onboarding/step4/connector-status`;
    return this.http.get<any>(url, { headers: this.headers });
  }

  /**
   * Test a connector after OAuth approval
   */
  testConnector(connectorId: string): Observable<any> {
    this.headers = this.getHeaders();
    return this.http.post<any>(
      this.getUrl() + `onboarding/step4/test-connector/${connectorId}`,
      {},
      { headers: this.headers }
    );
  }

  /**
   * Check if user can access a specific step
   */
  canAccessStep(stepNumber: number): Observable<{ canAccess: boolean; currentStep: number; stepCompleted: boolean; emailVerified: boolean; phoneVerified: boolean }> {
    this.headers = this.getHeaders();
    return this.http.get<{ canAccess: boolean; currentStep: number; stepCompleted: boolean; emailVerified: boolean; phoneVerified: boolean }>(
      this.getUrl() + `onboarding/step/${stepNumber}/can-access`,
      { headers: this.headers }
    ).pipe(
      catchError(error => {
        console.error('Error checking step access:', error);
        // On error, only allow access to step 1
        return of({
          canAccess: stepNumber === 1,
          currentStep: 1,
          stepCompleted: false,
          emailVerified: false,
          phoneVerified: false
        });
      })
    );
  }

  /**
   * Delete onboarding state (cleanup)
   */
  deleteOnboardingState(): Observable<string> {
    this.headers = this.getHeaders();
    return this.http.delete(
      this.getUrl() + 'onboarding/state',
      { headers: this.headers, responseType: 'text' }
    );
  }

  /**
   * Get Teams manifest for Step 5 (creates Internal connector if needed)
   */
  getTeamsManifest(permissionLevel: string): Observable<HttpResponse<Blob>> {
    this.headers = this.getHeaders();
    return this.http.get(
      this.getUrl() + `onboarding/step5/manifest/${permissionLevel}`,
      { headers: this.headers, responseType: 'blob', observe: 'response' }
    );
  }

  /**
   * Get Teams approval URL for Step 5 (creates Internal connector and bot channel if needed)
   */
  getTeamsApprovalUrl(permissionLevel: string, redirectUri: string, state: string): Promise<string> {
    this.headers = this.getHeaders();
    const params = new URLSearchParams();
    if (redirectUri) params.append('redirectUri', redirectUri);
    if (state) params.append('state', state);

    const url = this.getUrl() + `onboarding/step5/approvalurl/${permissionLevel}${params.toString() ? '?' + params.toString() : ''}`;

    return this.http.get(
      url,
      { headers: this.headers, responseType: 'text' }
    ).toPromise();
  }

  /**
   * Verify Teams installation for Step 5
   */
  verifyTeamsInstallation(): Observable<{ success: boolean; message: string }> {
    this.headers = this.getHeaders();
    const url = this.getUrl() + 'onboarding/step5/verify';

    return this.http.get<{ success: boolean; message: string }>(
      url,
      { headers: this.headers }
    );
  }

  /**
   * Get Slack approval URL for Step 5
   */
  getSlackApprovalUrl(redirectUri: string, state: string): Promise<string> {
    this.headers = this.getHeaders();
    const params = new URLSearchParams();
    if (redirectUri) params.append('redirectUri', redirectUri);
    if (state) params.append('state', state);

    const url = this.getUrl() + `onboarding/step5/slackapprovalurl?${params.toString()}`;

    return this.http.get(
      url,
      { headers: this.headers, responseType: 'text' }
    ).toPromise();
  }

  /**
   * Send channel approval invitation to delegate
   * @param channelRole Optional - 'Internal' for service channels, 'Customer' for client channels
   * @param channelId Optional - BotChannelID for existing channels
   * @param channelName Optional - Channel name for client channels
   */
  sendChannelApprovalInvitation(
    delegateEmail: string,
    channelType: 'teams' | 'slack',
    onboardingUserEmail: string,
    companyName: string,
    channelRole?: string,
    botId?: string,
    channelId?: string,
    channelName?: string
  ): Observable<any> {
    this.headers = this.getHeaders();
    const url = this.getUrl() + 'onboarding/sendChannelApprovalInvitation';

    const payload: any = {
      delegateEmail,
      channelType,
      onboardingUserEmail,
      companyName
    };

    // Add optional parameters if provided
    if (channelRole) {
      payload.channelRole = channelRole;
    }
    if (botId) {
      payload.botId = botId;
    }
    if (channelId) {
      payload.channelId = channelId;
    }
    if (channelName) {
      payload.channelName = channelName;
    }

    return this.http.post(url, payload, { headers: this.headers });
  }

  // Helper methods for validation
  validateStep1(step1Data: Step1Model): string[] {
    const errors: string[] = [];
    
    if (!step1Data.partnerName || step1Data.partnerName.trim() === '') {
      errors.push('Partner Name is required');
    }
    
    if (!step1Data.contactEmail || step1Data.contactEmail.trim() === '') {
      errors.push('Contact Email is required');
    } else if (!this.isValidEmail(step1Data.contactEmail)) {
      errors.push('Invalid email format');
    }
    
    return errors;
  }

  validateStep2(step2Data: Step2Model): string[] {
    const errors: string[] = [];
    
    if (!step2Data.selectedPsaId || step2Data.selectedPsaId.trim() === '') {
      errors.push('PSA selection is required');
    }
    
    return errors;
  }

  validateStep4(step4Data: Step4Model): string[] {
    const errors: string[] = [];

    if (!step4Data.selectedChannelType || step4Data.selectedChannelType.trim() === '') {
      errors.push('Client channel selection is required');
    }

    if (!step4Data.channelName || step4Data.channelName.trim() === '') {
      errors.push('Channel name is required');
    }

    return errors;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}